import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import { App } from './app/App';
import { AuthErrorBoundary } from './components/AuthErrorBoundary';
import { Auth0ProviderWithNavigate } from './features/auth/Auth0ProviderWithNavigate';
import { store } from './store';
import './index.css';

// Connect RPC codes that are definitive failures — no point retrying.
const NO_RETRY_CONNECT_CODES = new Set([
  5, // NotFound
  7, // PermissionDenied
  16, // Unauthenticated
]);

function isDefinitiveConnectError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'number' &&
    NO_RETRY_CONNECT_CODES.has((error as { code: number }).code)
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount, error) => {
        if (isDefinitiveConnectError(error)) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: (query) =>
        query.state.status !== 'error' || !isDefinitiveConnectError(query.state.error),
      refetchOnMount: (query) =>
        query.state.status !== 'error' || !isDefinitiveConnectError(query.state.error),
    },
  },
});

// Auth0 configuration
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const auth0Audience = import.meta.env.VITE_AUTH0_AUDIENCE;

// Validate Auth0 configuration
if (!auth0Domain || !auth0ClientId) {
  console.error('Auth0 configuration missing. Please check your .env file.');
  console.error('Required environment variables:');
  console.error('- VITE_AUTH0_DOMAIN');
  console.error('- VITE_AUTH0_CLIENT_ID');
  console.error('- VITE_AUTH0_AUDIENCE (recommended)');
  throw new Error('Auth0 domain and client ID must be set in .env file');
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Check if we're on HTTPS or localhost with HTTPS
const isSecureContext =
  window.location.protocol === 'https:' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

// Use createRoot (not hydrateRoot): this app is an authenticated SPA where all content
// is behind AuthGuard. SSR cannot pre-render authenticated content, so hydrateRoot
// against a server-rendered shell produces cascading hydration mismatches.
createRoot(rootElement).render(
  <StrictMode>
    <AuthErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {isSecureContext && auth0Domain && auth0ClientId ? (
              <Auth0ProviderWithNavigate
                domain={auth0Domain}
                clientId={auth0ClientId}
                audience={auth0Audience}
              >
                <App />
              </Auth0ProviderWithNavigate>
            ) : (
              <>
                {!isSecureContext && (
                  <div className="border-b border-yellow-200 bg-yellow-50 px-4 py-3">
                    <div className="mx-auto max-w-7xl text-sm text-yellow-800">
                      ⚠️ Development mode: Auth0 disabled (requires HTTPS)
                    </div>
                  </div>
                )}
                <App />
              </>
            )}
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Provider>
    </AuthErrorBoundary>
  </StrictMode>,
);
